/**
 * The Search tool — one box that finds features by value across layers.
 *
 * HOW IT DIFFERS FROM QUERY, WHICH SITS BESIDE IT
 * Query asks someone to BUILD a condition: pick a layer, pick a field, pick an
 * operator, then type. That is the right shape when you know how the data is
 * stored. It is the wrong shape when you are holding a meter number off a work
 * order — there, three of those four choices are questions the person cannot
 * answer. So this takes the value alone and looks for it in every field of
 * every layer the admin allowed.
 *
 * WHAT EACH ROW SAYS
 * With several layers in scope, a row names the layer it came from and the
 * field it matched — without that, two rows reading "Sector 14" are a coin
 * flip. With ONE layer that line would repeat on every row, so it is dropped
 * and the list reads like an ordinary suggestion list. Six rows by default,
 * shared round-robin across the layers so every one of them is represented.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import ToolPanelHeader from '../ToolPanel';
import { cfg, scopedLayers, type ToolContext } from './types';
import { EmptyHint, Notice, ResultHeader, ResultRow, ScrollList, inputClass } from './shared';
import { fetchLayerFieldNames, fetchNumericFieldNames } from '../../../tools/toolFields';
import {
  searchFeatures,
  type SearchHit,
  type SearchTarget,
} from '../../../services/featureSearchService';
import type { AppModule } from '../../../types';

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

type Status = 'idle' | 'loading' | 'ready' | 'error';

/** Every coordinate's bounding box, for framing whatever was chosen. */
function boundsOf(geometry: GeoJSON.Geometry | null | undefined) {
  if (!geometry) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;

  const walk = (value: any): void => {
    if (!Array.isArray(value)) return;
    if (typeof value[0] === 'number' && typeof value[1] === 'number') {
      west = Math.min(west, value[0]);
      east = Math.max(east, value[0]);
      south = Math.min(south, value[1]);
      north = Math.max(north, value[1]);
      return;
    }
    for (const part of value) walk(part);
  };
  walk((geometry as any).coordinates);

  return Number.isFinite(west) ? { west, south, east, north } : null;
}

/**
 * Where to put the map for one feature.
 *
 * A zoom derived from the feature's own extent, not a fixed number: the same
 * tool finds a valve and a district, and 17 is right for one and absurd for the
 * other. A point has no extent, so the floor below decides for it.
 */
function viewFor(geometry: GeoJSON.Geometry | null | undefined) {
  const box = boundsOf(geometry);
  if (!box) return null;

  const centre: [number, number] = [(box.south + box.north) / 2, (box.west + box.east) / 2];
  const span = Math.max(box.east - box.west, box.north - box.south);
  if (span < 1e-6) return { centre, zoom: 18 };

  // A little wider than the shape itself, so it is not pinned to the edges.
  return { centre, zoom: Math.max(2, Math.min(18, Math.floor(Math.log2(360 / span)) - 1)) };
}

export function SearchPanel({ ctx }: { ctx: ToolContext }) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);

  const [fieldsByLayer, setFieldsByLayer] = useState<Record<string, string[]>>({});
  const [schemaReady, setSchemaReady] = useState(false);
  const [numericByLayer, setNumericByLayer] = useState<Record<string, Set<string>>>({});

  const inputRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const placeholder = cfg<string>(ctx.config, 'placeholder', 'Search features…');
  const limit = Math.max(1, Math.min(20, cfg<number>(ctx.config, 'maxSuggestions', 6)));
  const zoomOnSelect = ctx.config.zoomOnSelect !== false;

  const scoped = useMemo(() => scopedLayers(ctx.config, ctx.layers), [ctx.config, ctx.layers]);
  const scopeKey = scoped.map((layer) => layer.id).join('|');
  /** More than one layer in scope, so a row has to say which one it is. */
  const showLayerName = scoped.length > 1;

  // The panel only exists because someone opened it, so the schema is fetched
  // straight away rather than waiting for the first keystroke.
  useEffect(() => {
    if (scoped.length === 0) {
      setSchemaReady(true);
      return undefined;
    }

    let cancelled = false;
    setSchemaReady(false);

    void (async () => {
      const names: Record<string, string[]> = {};
      const numeric: Record<string, Set<string>> = {};
      const context = {
        ...ctx.module,
        geoServerUrl: ctx.geoServerUrl,
        arcgisUrl: ctx.arcgisUrl,
        layers: ctx.layers,
      } as AppModule;

      await Promise.all(
        scoped.map(async (layer) => {
          names[layer.id] = await fetchLayerFieldNames(layer, context);
          numeric[layer.id] = await fetchNumericFieldNames(layer, context);
        }),
      );

      if (cancelled) return;
      setFieldsByLayer(names);
      setNumericByLayer(numeric);
      setSchemaReady(true);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, ctx.geoServerUrl, ctx.arcgisUrl]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const targets = useMemo<SearchTarget[]>(() => {
    const configured = Array.isArray(ctx.config.fields) ? (ctx.config.fields as string[]) : [];

    return scoped.map((layer) => {
      const known = fieldsByLayer[layer.id] ?? [];
      /* Discovery can come back empty — a service that would not answer, a WMS
         with no WFS beside it. Falling back to the configured names beats
         searching nothing: at worst that one layer's request is refused. */
      const fields =
        configured.length > 0
          ? known.length > 0
            ? known.filter((name) => configured.includes(name))
            : configured
          : known;

      return { layer, fields, numericFields: numericByLayer[layer.id] ?? new Set<string>() };
    });
  }, [scoped, fieldsByLayer, numericByLayer, ctx.config.fields]);

  /* Debounced, and the in-flight request is aborted on every change — which
     covers both "don't fetch per keystroke" and "ignore a slow early reply
     that would otherwise overwrite newer results". */
  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length < MIN_QUERY || !schemaReady) {
      abortRef.current?.abort();
      setHits([]);
      setStatus('idle');
      setError(null);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setStatus('loading');
      setError(null);

      searchFeatures(
        {
          term: trimmed,
          targets,
          geoServerUrl: ctx.geoServerUrl,
          arcgisUrl: ctx.arcgisUrl,
          limit,
        },
        controller.signal,
      )
        .then((found) => {
          if (controller.signal.aborted) return;
          setHits(found);
          setStatus('ready');
        })
        .catch((caught: unknown) => {
          // Aborting is the expected outcome of typing again, not a failure.
          if (controller.signal.aborted || (caught as Error)?.name === 'AbortError') return;
          console.error('[search] feature search failed', caught);
          setError(caught instanceof Error ? caught.message : 'Unable to search');
          setStatus('error');
        });
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query, targets, schemaReady, ctx.geoServerUrl, ctx.arcgisUrl, limit]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const choose = useCallback(
    (hit: SearchHit) => {
      setChosen(hit.key);

      /* The same selection channel the attribute table and the Query tool use,
         so the found feature is highlighted on the map — and ticked in a table
         that happens to be open — without this panel knowing either exists. */
      ctx.onSelectionChange?.([
        {
          id: String(hit.feature.id ?? hit.key),
          properties: (hit.feature.properties ?? {}) as Record<string, unknown>,
          geometry: hit.feature.geometry ?? undefined,
        },
      ]);

      if (!zoomOnSelect) return;
      const view = viewFor(hit.feature.geometry);
      // A record with no shape can still be selected; framing it would move the
      // map to nowhere, so the zoom is skipped rather than faked.
      if (view) ctx.onGoToLocation(view.centre, view.zoom);
    },
    [ctx, zoomOnSelect],
  );

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && hits.length > 0) {
      event.preventDefault();
      choose(hits[0]);
    }
  };

  const searchable = targets.some((target) => target.fields.length > 0);

  return (
    <>
      <ToolPanelHeader
        title="Search"
        description={
          showLayerName
            ? `Find a value across ${scoped.length} layers.`
            : 'Find a feature by any of its values.'
        }
        onClose={ctx.onClose}
      />

      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <input
          ref={inputRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
          className={`${inputClass} pl-8`}
        />
      </div>

      {scoped.length === 0 && (
        <Notice tone="warn">
          This map has no layers to search. Add one, then set the tool up in the Tool Library.
        </Notice>
      )}

      {scoped.length > 0 && schemaReady && !searchable && (
        <Notice tone="warn">
          None of the chosen layers reported any fields, so there is nothing to search. Check the
          service, or pick different layers in the Tool Library.
        </Notice>
      )}

      {!schemaReady && scoped.length > 0 && (
        <p className="flex items-center gap-2 text-[12px] text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Reading the layers…
        </p>
      )}

      {schemaReady && status === 'idle' && (
        <EmptyHint>
          Type at least {MIN_QUERY} characters. The closest {limit} matches appear here
          {showLayerName ? ', with the layer each one came from.' : '.'}
        </EmptyHint>
      )}

      {status === 'loading' && (
        <p className="flex items-center gap-2 text-[12px] text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Searching…
        </p>
      )}

      {status === 'error' && <Notice tone="warn">{error} — please try again.</Notice>}

      {status === 'ready' && hits.length === 0 && (
        <EmptyHint>
          Nothing matches “{query.trim()}”. Try part of the value rather than all of it.
        </EmptyHint>
      )}

      {status === 'ready' && hits.length > 0 && (
        <div className="space-y-1">
          <ResultHeader label="Matches" count={hits.length} />
          <ScrollList max={260}>
            {hits.map((hit) => (
              <ResultRow
                key={hit.key}
                title={hit.label}
                detail={showLayerName ? `${hit.layerName} · ${hit.field}` : undefined}
                active={chosen === hit.key}
                onClick={() => choose(hit)}
              />
            ))}
          </ScrollList>
        </div>
      )}
    </>
  );
}
